import { LEGACY_IMPORTED_SOURCE, LEGACY_IMPORTED_LABEL } from '../../lib/legacyImportedMessages';
// BL-177 Step 2d (section 7, board 06): one chat bubble.
//   ours = teal, theirs = slate; meta line "Val Whitney · 7:42 AM" (only the time is dim).
//   Delivery states show ONLY here, on the message, always with words:
//     Not delivered (red)  — the reason + Retry (the claim-once retry from #637, the imported provider texts only)
//     Unconfirmed (orange) — "check delivery before resending" and no one-click resend
//     Scheduled (purple, dashed) — "Scheduled for Wed Oct 14, 8:00 AM ET by Val" with live Edit / Send now / Cancel (Step 3).
//     Cancelled (grey, dashed) — the system stopped it (recipient opted out, or they replied first); the reason shows.
import React, { useMemo } from 'react';
import { waitingForReconnection, whatsappChecks, RECONNECTION_TOOLTIP } from '../../lib/reconnectionStatus';
import { outboundCallSummary } from '../../lib/outboundCall';
import StatusChip from '../ui/StatusChip';
import Button3D from '../ui/Button3D';
import { TONES } from '../../lib/colorMeaning';
import { bubbleMeta, deliveryMeaning, formatMessageTime, messageDeliveryState } from '../../lib/communicationsDisplay';
import { describeScheduledStatus, formatScheduledWhen } from '../../lib/scheduledSend';
import ScheduledHistory from './ScheduledHistory';
import { sanitizeEmailHtml } from '../../lib/emailHtml';

// Bubble fills are deep tints of each tone so white text stays >= 4.5:1; the border carries the tone's bright edge.
const BUBBLE_STYLES = Object.freeze({
    outbound: { background: TONES.teal.bg, borderColor: TONES.teal.border },
    inbound: { background: TONES.slate.bg, borderColor: TONES.slate.border },
    not_delivered: { background: '#4C1515', borderColor: TONES.red.border },
    unconfirmed: { background: '#4A2209', borderColor: TONES.orange.border },
    scheduled: { background: '#2E1065', borderColor: TONES.purple.border, borderStyle: 'dashed', borderWidth: 2 },
    cancelled: { background: '#1F2937', borderColor: TONES.slate.border, borderStyle: 'dashed', borderWidth: 2 },
});

// The one dim thing in a bubble is its time. Lighter than the page's dim grey so it still reads >= 4.5:1 on every
// bubble fill (the deepest is teal #0F766E).
const TIME_STYLE = Object.freeze({ color: 'var(--ia-comms-bubble-meta)' });

export const UNCONFIRMED_BUBBLE_TEXT = 'check delivery before resending';

function MessageBubble({
    item,
    staffNames = {},
    participantNames = {},
    showProgress = false,
    reconnectionMinutes = 15,
    receiptHistory = [],
    now = Date.now(),
    children,
    onRetry,
    retrying = false,
    retryDisabled = false,
    // BL-177 Step 3: what the person may do with a scheduled message, and what each button does.
    scheduledControls = null,
    theme = 'light',
}) {
    const outbound = item?.direction === 'outbound';
    const reconnecting = waitingForReconnection(item, { now, minutes: reconnectionMinutes, history: receiptHistory });
    const checks = outbound && item.channel === 'whatsapp' ? whatsappChecks(item.provider_status) : null;
    const state = messageDeliveryState(item);
    const meaning = deliveryMeaning(state);
    const meta = bubbleMeta(item, { staffNames, participantNames, showProgress });
    const style = BUBBLE_STYLES[state] || (outbound ? BUBBLE_STYLES.outbound : BUBBLE_STYLES.inbound);
    const canRetry = false; // Imported failures must never be replayed through another provider.
    const row = item?.scheduledRow || null;
    const zone = row?.company_time_zone || 'America/New_York';
    const scheduledFor = state === 'scheduled' ? formatScheduledWhen(item?.scheduled_for || item?.scheduledFor || item?.at, zone) : '';
    const waiting = state === 'scheduled' && row ? describeScheduledStatus(row, zone) : '';
    const isWaiting = Boolean(waiting) && !/^Scheduled for /.test(waiting);
    const busy = Boolean(scheduledControls?.busy);
    // Review fix round 1: the stored HTML is sanitized once per change, not on every re-render of the thread.
    const emailHtml = item?.channel === 'email' ? item?.emailHtml : '';
    const safeEmailHtml = useMemo(() => (emailHtml ? sanitizeEmailHtml(emailHtml) : ''), [emailHtml]);

    if (item?.systemLine) {
        return (
            <p
                className={`max-w-[90%] rounded-full border px-3 py-1 text-center text-xs ${
                    theme === 'dark'
                        ? 'border-gray-700 bg-gray-900 text-gray-400'
                        : 'border-gray-200 bg-gray-100 text-gray-500'
                }`}
                role="status"
                data-thread-system-line="true"
            >
                {children}
                {item?.systemKind === 'ai_handoff' && formatMessageTime(item?.at) && ` · ${formatMessageTime(item.at)}`}
            </p>
        );
    }

    return (
        <div
            data-message-bubble={outbound ? 'outbound' : 'inbound'}
            data-delivery-state={state || undefined}
            data-pending={item?.pending ? 'true' : undefined}
            className={`min-w-0 max-w-full rounded-2xl border px-4 py-2.5 text-[15px] text-white whitespace-pre-wrap break-words ${item?.pending ? 'opacity-75' : ''}`}
            style={{ ...style, color: 'var(--ia-comms-bubble-ink, #FFFFFF)' }}
        >
            {item?.eventName && <p className="mb-1 text-xs font-semibold" data-event-tag="true">Event / service update · {item.eventName}</p>}
            {item?.channel === 'email' && (item.emailSubject || item.emailCc?.length || item.emailBcc?.length) && (
                // 1.12.212-beta: an email shows its subject and who else got it. Bcc is staff-only (this thread is a staff screen).
                <div className="mb-1.5 border-b border-white/25 pb-1.5 text-sm whitespace-normal" data-email-header="true">
                    {item.emailSubject && <p className="font-semibold" data-email-subject="true">{item.emailSubject}</p>}
                    {item.emailCc?.length > 0 && <p className="text-xs" data-email-cc="true">Cc: {item.emailCc.join(', ')}</p>}
                    {item.emailBcc?.length > 0 && <p className="text-xs" data-email-bcc="true">Bcc (staff only): {item.emailBcc.join(', ')}</p>}
                </div>
            )}
            {/* An outbound call's card below already says "Outbound call · by … · …"; its stored body would only repeat it. */}
            {item?.channel === 'email' && item.emailHtml
                // A formatted email shows its formatting — sanitized again here, whatever is stored (memoized above).
                ? <div className="ia-email-html whitespace-normal" data-email-html="true" dangerouslySetInnerHTML={{ __html: safeEmailHtml }} />
                : !(item?.channel === 'voice' && item?.direction === 'outbound') && <div>{children}</div>}
            {item?.messageSource === LEGACY_IMPORTED_SOURCE && <span className="mt-1 block text-xs font-semibold text-white">{LEGACY_IMPORTED_LABEL}</span>}
            {item?.channel === 'voice' && (
                <div className="mt-2 rounded-lg border border-white/20 bg-black/10 px-3 py-2 text-sm" data-call-log="true">
                    {item.direction === 'outbound' ? (
                        <p className="font-semibold" data-call-direction="outbound">
                            {outboundCallSummary({ staffName: item.staffName, duration: item.callDuration, status: item.callStatus })}
                        </p>
                    ) : (
                    <p className="font-semibold">
                        {item.callStatus === 'voicemail'
                            ? 'Voicemail'
                            : item.callStatus === 'answered'
                                ? `Answered${item.answeredByName ? ` by ${item.answeredByName}` : ''}`
                                : item.callStatus === 'ringing'
                                    ? 'Ringing'
                                    : item.callStatus === 'completed'
                                        ? 'Call ended'
                                        : 'Missed call'}
                        {Number.isFinite(item.callDuration) && item.callDuration > 0 ? ` · ${item.callDuration}s` : ''}
                    </p>
                    )}
                    {item.transcript && <p className="mt-1 whitespace-pre-wrap text-sm" data-call-transcript="true">Transcript: {item.transcript}</p>}
                </div>
            )}
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 whitespace-normal text-xs text-white" data-bubble-meta="true">
                {state === 'scheduled' ? (
                    <span data-scheduled-line="true">
                        <span aria-hidden="true">🕒 </span>Scheduled for <span style={TIME_STYLE}>{scheduledFor}</span>{meta.who ? ` by ${meta.who}` : ''}
                    </span>
                ) : state === 'cancelled' ? (
                    <span data-cancelled-line="true">{item?.scheduledRow?.status_reason || 'Cancelled'}</span>
                ) : (
                    <>
                        {meta.who && <span>{meta.who}</span>}
                        {meta.who && meta.time && <span aria-hidden="true">·</span>}
                        {meta.time && <span style={TIME_STYLE} data-bubble-time="true">{meta.time}</span>}
                        {meta.status && <span aria-hidden="true">·</span>}
                        {meta.status && !reconnecting && <span data-bubble-status="true">{meta.status}</span>}
                        {checks && <span aria-label={checks.label} data-read={checks.read ? 'true' : 'false'} data-whatsapp-checks="true">{checks.text}</span>}
                        {reconnecting && <span className="ia-reconnection-status" tabIndex={0} title={RECONNECTION_TOOLTIP} aria-label={`Waiting for reconnection. ${RECONNECTION_TOOLTIP}`} data-reconnection-status="true">Waiting for reconnection</span>}
                    </>
                )}
            </div>
            {state === 'not_delivered' && (
                <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2 whitespace-normal" role="status" data-send-failed="true">
                    <StatusChip tone={meaning.tone}>{meaning.label}</StatusChip>
                    <span className="min-w-0 text-xs text-white" title={item.provider_error_message || undefined} data-send-failed-reason="true">{item.provider_error_message || 'The text did not go out.'}</span>
                    {canRetry && (
                        <Button3D
                            variant="light"
                            className="ia-comms-pill ml-auto"
                            disabled={retryDisabled}
                            onClick={() => onRetry(item)}
                            aria-label="Retry sending this text"
                        >
                            {retrying ? 'Retrying…' : 'Retry'}
                        </Button3D>
                    )}
                </div>
            )}
            {state === 'unconfirmed' && (
                <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2 whitespace-normal" role="status" data-send-unconfirmed="true">
                    <StatusChip tone={meaning.tone}>{meaning.label}</StatusChip>
                    <span className="text-xs text-white">{UNCONFIRMED_BUBBLE_TEXT}</span>
                </div>
            )}
            {state === 'scheduled' && (
                <div className="mt-2 flex min-w-0 flex-col gap-1.5 whitespace-normal" data-scheduled-actions="true">
                    {isWaiting && <p className="text-xs text-[#FDE68A]" data-scheduled-waiting="true">{waiting}</p>}
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <StatusChip tone={meaning.tone}>{meaning.label}</StatusChip>
                        <Button3D variant="light" className="ia-comms-pill" disabled={busy || !scheduledControls?.canEdit} onClick={() => scheduledControls?.onEdit?.(item)} aria-label="Edit scheduled message">Edit</Button3D>
                        <Button3D variant="light" className="ia-comms-pill" disabled={busy || !scheduledControls?.canEdit} onClick={() => scheduledControls?.onSendNow?.(item)} aria-label="Send scheduled message now">Send now</Button3D>
                        <Button3D variant="light" className="ia-comms-pill" disabled={busy || !scheduledControls?.canCancel} onClick={() => scheduledControls?.onCancel?.(item)} aria-label="Cancel scheduled message">Cancel</Button3D>
                    </div>
                    {row?.cancel_on_reply && <p className="text-xs" data-cancel-on-reply="true">Will be cancelled if they reply first.</p>}
                    {scheduledControls?.error && <p role="alert" className="text-xs text-[#FCA5A5]" data-scheduled-error="true">{scheduledControls.error}</p>}
                    {row && <ScheduledHistory row={row} loadEvents={scheduledControls?.loadEvents} staffNames={staffNames} timeZone={zone} />}
                </div>
            )}
            {state === 'cancelled' && row && (
                <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2 whitespace-normal" role="status" data-scheduled-cancelled="true">
                    <StatusChip tone={meaning.tone}>{meaning.label}</StatusChip>
                    <ScheduledHistory row={row} loadEvents={scheduledControls?.loadEvents} staffNames={staffNames} timeZone={zone} />
                </div>
            )}
            {outbound && item?.retried && (
                <div className="mt-1 text-xs text-white">Retried below</div>
            )}
        </div>
    );
}

export default MessageBubble;
